/**
 * Security-focused cache management
 * Handles user transitions and prevents data leakage
 * 
 * CRITICAL SECURITY RESPONSIBILITIES:
 * - Clear cache data on user logout/login
 * - Prevent cross-user cache access during user transitions
 * - Validate cache integrity and detect security violations
 * - Handle safe cache restoration with user validation
 * - Manage user-specific cache operations
 */
import { QueryClient } from '@tanstack/react-query';
import { UserAwareCacheKeys } from './userAwareCacheKeys';

export interface CacheSecurityOptions {
  strictMode?: boolean; // Throw errors on security violations
  logViolations?: boolean; // Log potential security issues
  enableDebugLogging?: boolean; // Enable detailed debug logging
}

export interface CacheViolation {
  type: 'foreign_cache' | 'legacy_cache' | 'invalid_user_context';
  queryKey: readonly unknown[];
  details: string;
  timestamp: number;
}

export class CacheSecurityManager {
  private queryClient: QueryClient;
  private options: CacheSecurityOptions;
  private violations: CacheViolation[] = [];

  constructor(queryClient: QueryClient, options: CacheSecurityOptions = {}) {
    this.queryClient = queryClient;
    this.options = {
      strictMode: true,
      logViolations: true,
      enableDebugLogging: process.env.NODE_ENV === 'development',
      ...options
    };

    if (this.options.enableDebugLogging) {
      console.log('[CacheSecurityManager] Initialized with options:', this.options);
    }
  }

  /**
   * Clear all cache data for a specific user
   * Used when user logs out or switches accounts
   */
  clearUserCache(userId: string): void {
    if (!userId) {
      console.error('[CacheSecurityManager] Cannot clear cache: Invalid user ID');
      return;
    }

    const userIdPreview = userId.substring(0, 20) + '...';
    console.log(`🧹 [CacheSecurityManager] Clearing all cache for user: ${userIdPreview}`);

    // Get all cache keys that belong to this user
    const userCacheKeyPrefixes = UserAwareCacheKeys.getAllUserCacheKeyPrefixes(userId);
    
    let clearedCount = 0;
    userCacheKeyPrefixes.forEach(keyPrefix => {
      try {
        this.queryClient.removeQueries({ queryKey: keyPrefix });
        clearedCount++;
        
        if (this.options.enableDebugLogging) {
          console.log(`[CacheSecurityManager] Cleared cache for key prefix:`, keyPrefix);
        }
      } catch (error) {
        console.error(`[CacheSecurityManager] Error clearing cache for key ${JSON.stringify(keyPrefix)}:`, error);
      }
    });

    console.log(`✅ [CacheSecurityManager] Cleared ${clearedCount} cache key prefixes for user`);
  }

  /**
   * Clear all cache data that doesn't belong to the current user
   * Critical for preventing data leakage during user transitions
   */
  clearForeignUserCache(currentUserId: string): void {
    if (!currentUserId) {
      console.error('[CacheSecurityManager] Cannot clear foreign cache: Invalid current user ID');
      return;
    }

    const userIdPreview = currentUserId.substring(0, 20) + '...';
    console.log(`🔒 [CacheSecurityManager] Clearing foreign cache (keeping data for user: ${userIdPreview})`);

    const allQueries = this.queryClient.getQueryCache().getAll();
    let clearedCount = 0;
    let keptCount = 0;

    allQueries.forEach(query => {
      const queryKey = query.queryKey;
      
      // Check if this cache entry belongs to the current user
      if (UserAwareCacheKeys.belongsToUser(queryKey, currentUserId)) {
        keptCount++;
        return; // Keep this cache entry
      }

      // Check if this is a global cache entry (non-user-specific)
      if (UserAwareCacheKeys.isGlobalCacheKey(queryKey)) {
        keptCount++;
        return; // Keep global cache entries
      }

      // Check if this is a user-specific cache entry for a different user
      const foreignUserId = UserAwareCacheKeys.extractUserIdFromKey(queryKey);
      if (foreignUserId && foreignUserId !== currentUserId) {
        this.recordViolation({
          type: 'foreign_cache',
          queryKey,
          details: `Foreign user cache detected: ${foreignUserId.substring(0, 20)}...`,
          timestamp: Date.now()
        });

        if (this.options.logViolations && this.options.enableDebugLogging) {
          console.log(`🗑️ [CacheSecurityManager] Removing foreign user cache:`, {
            queryKey: JSON.stringify(queryKey),
            foreignUserId: foreignUserId.substring(0, 20) + '...'
          });
        }
        
        this.queryClient.removeQueries({ queryKey });
        clearedCount++;
        return;
      }

      // Legacy cache entries without user context - clear them for security
      if (UserAwareCacheKeys.isLegacyCacheKey(queryKey)) {
        this.recordViolation({
          type: 'legacy_cache',
          queryKey,
          details: 'Legacy cache entry without user context',
          timestamp: Date.now()
        });

        if (this.options.logViolations && this.options.enableDebugLogging) {
          console.log(`🧹 [CacheSecurityManager] Cleaning legacy cache entry:`, JSON.stringify(queryKey));
        }
        
        this.queryClient.removeQueries({ queryKey });
        clearedCount++;
      }
    });

    if (clearedCount > 0) {
      console.log(`✅ [CacheSecurityManager] Cleared ${clearedCount} stale cache entries during user transition, kept ${keptCount} valid entries`);
    } else if (this.options.enableDebugLogging) {
      console.log(`✅ [CacheSecurityManager] Cache validation complete - all ${keptCount} entries valid for current user`);
    }
  }

  /**
   * Validate that all cache entries belong to the current user or are global
   * Returns detailed validation results for debugging
   */
  validateCacheIntegrity(currentUserId: string): { 
    isValid: boolean; 
    violations: CacheViolation[];
    summary: ReturnType<typeof UserAwareCacheKeys.generateDebugSummary>;
  } {
    const violations: CacheViolation[] = [];
    const allQueries = this.queryClient.getQueryCache().getAll();
    const allQueryKeys = allQueries.map(q => q.queryKey);

    allQueries.forEach(query => {
      const queryKey = query.queryKey;

      // Skip validation for global cache entries
      if (UserAwareCacheKeys.isGlobalCacheKey(queryKey)) {
        return;
      }

      // Check for foreign user cache entries
      const userId = UserAwareCacheKeys.extractUserIdFromKey(queryKey);
      if (userId && userId !== currentUserId) {
        violations.push({
          type: 'foreign_cache',
          queryKey,
          details: `Cache entry belongs to different user: ${userId.substring(0, 20)}...`,
          timestamp: Date.now()
        });
      }

      // Check for legacy cache entries
      if (UserAwareCacheKeys.isLegacyCacheKey(queryKey)) {
        violations.push({
          type: 'legacy_cache',
          queryKey,
          details: 'Cache entry lacks user context',
          timestamp: Date.now()
        });
      }

      // Check for invalid user context
      if (!userId && !UserAwareCacheKeys.isGlobalCacheKey(queryKey)) {
        violations.push({
          type: 'invalid_user_context',
          queryKey,
          details: 'Cache entry missing user context',
          timestamp: Date.now()
        });
      }
    });

    const isValid = violations.length === 0;
    const summary = UserAwareCacheKeys.generateDebugSummary(allQueryKeys);
    
    if (!isValid && this.options.logViolations && this.options.enableDebugLogging) {
      console.log(`� [CacheSecurityManager] Cleaned ${violations.length} stale cache entries during user switch`);
    }

    return { isValid, violations, summary };
  }

  /**
   * Safe cache restoration that validates user context
   * Only restores cache entries that belong to the current user
   */
  safeRestoreCache(cacheData: any[], currentUserId: string): void {
    if (!currentUserId) {
      console.error('[CacheSecurityManager] Cannot restore cache: Invalid user ID');
      return;
    }

    const userIdPreview = currentUserId.substring(0, 20) + '...';
    console.log(`🔄 [CacheSecurityManager] Safely restoring cache for user: ${userIdPreview}`);

    let restoredCount = 0;
    let rejectedCount = 0;

    cacheData.forEach(entry => {
      const { queryKey, data, dataUpdatedAt, userId: cachedUserId } = entry;

      // Validate that this cache entry belongs to the current user
      if (cachedUserId && cachedUserId !== currentUserId) {
        rejectedCount++;
        this.recordViolation({
          type: 'foreign_cache',
          queryKey,
          details: `Attempted to restore foreign user cache: ${cachedUserId.substring(0, 20)}...`,
          timestamp: Date.now()
        });
        
        if (this.options.logViolations && this.options.enableDebugLogging) {
          console.log(`🧹 [CacheSecurityManager] Rejected foreign cache during restoration:`, {
            queryKey: JSON.stringify(queryKey),
            cachedUserId: cachedUserId.substring(0, 20) + '...'
          });
        }
        return;
      }

      // Double-check using query key structure
      if (!UserAwareCacheKeys.belongsToUser(queryKey, currentUserId) && 
          !UserAwareCacheKeys.isGlobalCacheKey(queryKey)) {
        rejectedCount++;
        this.recordViolation({
          type: 'invalid_user_context',
          queryKey,
          details: 'Cache entry structure does not match current user',
          timestamp: Date.now()
        });
        
        if (this.options.logViolations && this.options.enableDebugLogging) {
          console.log(`🧹 [CacheSecurityManager] Rejected invalid cache structure:`, JSON.stringify(queryKey));
        }
        return;
      }

      // Restore the cache entry
      try {
        this.queryClient.setQueryData(queryKey, data, {
          updatedAt: dataUpdatedAt
        });
        restoredCount++;
        
        if (this.options.enableDebugLogging) {
          console.log(`[CacheSecurityManager] Restored cache entry:`, JSON.stringify(queryKey));
        }
      } catch (error) {
        console.error(`[CacheSecurityManager] Error restoring cache entry:`, error);
        rejectedCount++;
      }
    });

    console.log(`✅ [CacheSecurityManager] Restored ${restoredCount} entries, rejected ${rejectedCount} entries`);
  }

  /**
   * Handle user logout - clear all cache
   * Most aggressive cache clearing for maximum security
   */
  handleUserLogout(): void {
    console.log('🚪 [CacheSecurityManager] User logout detected - clearing all cache');
    
    try {
      this.queryClient.clear();
      console.log('✅ [CacheSecurityManager] All cache cleared on logout');
    } catch (error) {
      console.error('[CacheSecurityManager] Error clearing cache on logout:', error);
    }
    
    // Clear violations history
    this.violations = [];
  }

  /**
   * Handle user switch - clear old user cache and foreign cache
   * Used when switching between user accounts
   */
  handleUserSwitch(previousUserId: string | null, newUserId: string): void {
    const prevUserPreview = previousUserId ? previousUserId.substring(0, 20) + '...' : 'null';
    const newUserPreview = newUserId.substring(0, 20) + '...';
    
    console.log(`🔄 [CacheSecurityManager] User switch detected: ${prevUserPreview} → ${newUserPreview}`);
    
    if (previousUserId) {
      this.clearUserCache(previousUserId);
    }
    
    // Clear any foreign cache that might exist
    this.clearForeignUserCache(newUserId);
    
    // Validate integrity after switch
    if (this.options.enableDebugLogging) {
      const validation = this.validateCacheIntegrity(newUserId);
      console.log('[CacheSecurityManager] Post-switch cache validation:', validation.summary);
    }
  }

  /**
   * Get cache security statistics for monitoring
   */
  getSecurityStats(): {
    totalViolations: number;
    violationsByType: { [key: string]: number };
    recentViolations: CacheViolation[];
    cacheIntegrity: boolean;
  } {
    const violationsByType: { [key: string]: number } = {};
    
    this.violations.forEach(violation => {
      violationsByType[violation.type] = (violationsByType[violation.type] || 0) + 1;
    });

    // Get recent violations (last 10 minutes)
    const tenMinutesAgo = Date.now() - (10 * 60 * 1000);
    const recentViolations = this.violations.filter(v => v.timestamp > tenMinutesAgo);

    return {
      totalViolations: this.violations.length,
      violationsByType,
      recentViolations,
      cacheIntegrity: recentViolations.length === 0
    };
  }

  /**
   * Record a security violation for monitoring and debugging
   */
  private recordViolation(violation: CacheViolation): void {
    this.violations.push(violation);
    
    // Keep only last 100 violations to prevent memory bloat
    if (this.violations.length > 100) {
      this.violations = this.violations.slice(-100);
    }

    if (this.options.strictMode && this.options.enableDebugLogging) {
      console.log('[CacheSecurityManager] Cache cleanup:', violation.type, violation.details);
    }
  }

  /**
   * Clear all recorded violations (for testing)
   */
  clearViolations(): void {
    this.violations = [];
  }
}